import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import ContactSection from "@/components/ContactSection";
import HeyflowBanner from "@/components/HeyflowBanner";
import ProvenExpertBadge from "@/components/ProvenExpertBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  HandHeart, 
  CheckCircle, 
  Users, 
  Calculator, 
  FileText, 
  Shield,
  TrendingUp,
  Home,
  Euro,
  Clock,
  Phone,
  Award
} from "lucide-react";
import janineHardiImage from "@/assets/janine-hardi.jpg";
import niessbrauchImage from "@/assets/niessbrauch.png";
import { Link } from "react-router-dom";

const Niessbrauch = () => {
  const advantages = [
    {
      icon: Users,
      title: "Schenkung zu Lebzeiten",
      description: "Durch die vorzeitige Übertragung an die nächste Generation können Eigentümer ihren Nachlass aktiv gestalten."
    },
    {
      icon: TrendingUp,
      title: "Steuervorteile",
      description: "Der Wert des Nießbrauchrechts mindert den steuerpflichtigen Immobilienwert und reduziert Schenkungs- und Erbschaftssteuern deutlich."
    },
    {
      icon: FileText,
      title: "Erbschaft geregelt",
      description: "Die Immobilie wird schon zu Lebzeiten übertragen – Streitigkeiten zwischen Erben lassen sich so vermeiden."
    },
    {
      icon: Home,
      title: "Wohn- und Nutzungsrecht bleibt",
      description: "Auch nach der Schenkung können Eigentümer weiterhin im Haus wohnen oder es vermieten."
    }
  ];

  const examples = [
    {
      title: "Einfamilienhaus in München",
      value: "1.200.000 €",
      niessbrauchValue: "400.000 €",
      taxRelevantValue: "800.000 €",
      benefit: "Deutliche Reduzierung der Schenkungssteuer"
    },
    {
      title: "Eigentumswohnung in Köln", 
      value: "600.000 €",
      niessbrauchValue: "200.000 €",
      taxRelevantValue: "400.000 €",
      benefit: "Mieteinnahmen bleiben bei den Eltern"
    },
    {
      title: "Mehrfamilienhaus in Nürnberg",
      value: "1.500.000 €", 
      niessbrauchValue: "500.000 €",
      taxRelevantValue: "1.000.000 €",
      benefit: "60.000 € jährliche Mieteinnahmen weiterhin gesichert"
    }
  ];

  const faqItems = [
    {
      question: "Kann ich trotz Nießbrauch verkaufen?",
      answer: "Nein, die Immobilie gehört bereits den Kindern. Nur die Eigentümer können verkaufen."
    },
    {
      question: "Kann ich die Immobilie vermieten?",
      answer: "Ja, als Nießbraucher dürfen Sie Mieten erheben und behalten."
    },
    {
      question: "Wie wird der Nießbrauchwert berechnet?", 
      answer: "Er richtet sich nach dem Alter des Berechtigten und der statistischen Lebenserwartung."
    },
    {
      question: "Müssen meine Kinder Steuern zahlen?",
      answer: "Ja, aber durch den Abzug des Nießbrauchwertes reduziert sich die Steuerlast erheblich."
    },
    {
      question: "Was passiert, wenn ich ins Pflegeheim ziehe?",
      answer: "Sie können die Immobilie weiterhin vermieten und Einnahmen erzielen."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Nießbrauchrecht: Haus übertragen & wohnen bleiben </title>
        <meta 
          name="description" 
          content="Nießbrauchrecht: Haus an Erben übertragen mit lebenslangem Wohnrecht. Steuerliche Vorteile nutzen & wohnen bleiben. Kostenlose Beratung zur Eigenheimverrentung." 
        />
        <meta name="keywords" content="niessbrauchrecht, nießbrauch, wohnrecht, haus verkaufen und wohnen bleiben, verkaufen aber wohnen bleiben, steuervorteile, schenkung, eigenheimverrentung" />
        <link rel="canonical" href="https://eigenheimverrentung.de/niessbrauch" />
        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Nießbrauchrecht: Haus übertragen & wohnen bleiben" />
        <meta property="og:description" content="Übertragung an Erben mit lebenslangem Wohn- und Nutznießrecht. Steuerliche Vorteile und Familienabsicherung kombinieren." />
        <meta property="og:url" content="https://eigenheimverrentung.de/niessbrauch" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Nießbrauchrecht: Haus übertragen & wohnen bleiben" />
        <meta name="twitter:description" content="Übertragung an Erben mit lebenslangem Wohn- und Nutznießrecht. Steuerliche Vorteile nutzen." />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Nießbrauchrecht – Übertragung mit lebenslangem Wohnrecht",
            "description": "Kompletter Ratgeber zum Nießbrauchrecht: Haus an Erben übertragen mit lebenslangem Wohnrecht und steuerlichen Vorteilen.",
            "author": {
              "@type": "Organization",
              "name": "eigenheimverrentung.de"
            },
            "publisher": {
              "@type": "Organization",
              "name": "eigenheimverrentung.de",
              "url": "https://eigenheimverrentung.de"
            },
            "datePublished": new Date().toISOString(),
            "dateModified": new Date().toISOString(),
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "https://eigenheimverrentung.de/niessbrauch"
            }
          })}
        </script>
        
        {/* FAQ Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Kann ich trotz Nießbrauch verkaufen?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Nein, die Immobilie gehört bereits den Kindern. Nur die Eigentümer können verkaufen."
                }
              },
              {
                "@type": "Question",
                "name": "Kann ich die Immobilie vermieten?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ja, als Nießbraucher dürfen Sie Mieten erheben und behalten."
                }
              },
              {
                "@type": "Question",
                "name": "Wie wird der Nießbrauchwert berechnet?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Er richtet sich nach dem Alter des Berechtigten und der statistischen Lebenserwartung."
                }
              },
              {
                "@type": "Question",
                "name": "Müssen meine Kinder Steuern zahlen?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ja, aber durch den Abzug des Nießbrauchwertes reduziert sich die Steuerlast erheblich."
                }
              },
              {
                "@type": "Question",
                "name": "Was passiert, wenn ich ins Pflegeheim ziehe?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Sie können die Immobilie weiterhin vermieten und Einnahmen erzielen."
                }
              }
            ]
          })}
        </script>

        {/* Organization & Local Business Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "RentePlusImmobilie - Eigenheimverrentung",
            "url": "https://eigenheimverrentung.de",
            "telephone": "+49-89-628246446",
            "email": "info@renteplusimmobilie.de",
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "DE"
            },
            "areaServed": "Deutschland",
            "serviceType": ["Leibrente", "Wohnrecht", "Nießbrauchrecht", "Teilverkauf", "Rückmiete", "Seniorenfinanzierung"],
            "description": "Kostenlose Beratung zur Eigenheimverrentung mit Nießbrauchrecht und steuerlichen Vorteilen."
          })}
        </script>

        {/* BreadcrumbList Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Startseite",
                "item": "https://eigenheimverrentung.de/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Nießbrauch",
                "item": "https://eigenheimverrentung.de/niessbrauch"
              }
            ]
          })}
        </script>

        {/* HowTo Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            "name": "Wie übertrage ich mein Haus mit Nießbrauch?",
            "description": "Schritt-für-Schritt Anleitung zur Übertragung einer Immobilie mit Nießbrauchrecht bei renteplusimmobilie.de",
            "totalTime": "P30D",
            "step": [
              {
                "@type": "HowToStep",
                "position": 1,
                "name": "Kostenlose Erstberatung",
                "text": "Im Gespräch wird geprüft, ob Nießbrauch, Leibrente, Teilverkauf oder Rückmiete besser passt."
              },
              {
                "@type": "HowToStep",
                "position": 2,
                "name": "Immobilienbewertung",
                "text": "Zur steuerlichen Optimierung ist eine genaue Wertermittlung der Immobilie notwendig."
              },
              {
                "@type": "HowToStep",
                "position": 3,
                "name": "Vertragsgestaltung",
                "text": "Der Nießbrauch wird im notariellen Schenkungsvertrag geregelt und im Grundbuch gesichert."
              },
              {
                "@type": "HowToStep",
                "position": 4,
                "name": "Übertragung & Absicherung",
                "text": "Die Immobilie wird übertragen, das Nießbrauchrecht im Grundbuch eingetragen."
              }
            ]
          })}
        </script>

        {/* Service Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Nießbrauchrecht",
            "name": "Nießbrauch - Familienorientierte Lösung",
            "description": "Übertragung der Immobilie an Erben mit lebenslangem Wohn- und Nutznießrecht. Steuerliche Vorteile durch vorzeitige Schenkung.",
            "provider": {
              "@type": "LocalBusiness",
              "name": "RentePlusImmobilie - Eigenheimverrentung",
              "url": "https://eigenheimverrentung.de",
              "telephone": "+49-89-628246446",
              "email": "info@renteplusimmobilie.de"
            },
            "areaServed": {
              "@type": "Country",
              "name": "Deutschland"
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Familienorientierte Immobilieneigentümer mit Fokus auf Erbschaftsplanung"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Nießbrauch Leistungen",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Steueroptimierte Übertragung",
                    "description": "Reduzierung der Schenkungs- und Erbschaftssteuer durch Nießbrauchrecht"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Grundbuchliche Absicherung",
                    "description": "Eintragung des Nießbrauchrechts im Grundbuch"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Mieteinnahmen behalten",
                    "description": "Weiterhin Mieteinnahmen erzielen trotz Übertragung"
                  }
                }
              ]
            },
            "url": "https://eigenheimverrentung.de/niessbrauch"
          })}
        </script>
      </Helmet>
      
      <div className="min-h-screen bg-background/70">
      
      <Header />
      
      <main>
        {/* Breadcrumb Navigation */}
        <section className="py-4 bg-muted/30 border-b">
          <div className="container-comfortable">
            <nav className="flex items-center gap-2 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-primary transition-colors">
                Startseite
              </Link>
              <span>/</span>
              <span className="text-primary font-medium">Nießbrauch</span>
            </nav>
          </div>
        </section>

        {/* Hero Section */}
        <section className="py-16 md:py-24 bg-gradient-subtle">
          <div className="container-comfortable">
            <div className="max-w-4xl mx-auto text-center">
              <Badge variant="secondary" className="mb-6 text-base px-4 py-2">
                Kostenlose Beratung
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold text-primary mb-6 text-balance">
                Nießbrauch – Familienorientierte Lösung
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 text-balance">
                Übertragung an Erben mit lebenslangem Wohn- und Nutznießrecht
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="text-xl px-8" onClick={() => window.open('https://calendly.com/renteplusimmobilie/20min', '_blank')}>
                  <Calculator className="h-5 w-5 mr-2" />
                  Kostenloses Telefongespräch
                </Button>
                <Button variant="outline" size="lg" className="text-xl px-8" onClick={() => window.open('tel:089628246446', '_self')}>
                  <Phone className="h-5 w-5 mr-2" />
                  089 62 82 46 46
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Image Section */}
        <section className="py-12 md:py-16">
          <div className="container-comfortable">
            <div className="max-w-2xl mx-auto text-center">
              <img 
                src={niessbrauchImage} 
                alt="Nießbrauchrecht - Haus übertragen und wohnen bleiben" 
                className="w-full max-w-md mx-auto rounded-xl shadow-lg"
              />
            </div>
          </div>
        </section>

        {/* What is Nießbrauch Section */}
        <section className="py-16 md:py-24">
          <div className="container-comfortable">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-8 text-center">
                Was ist Nießbrauch?
              </h2>
              
              <Card className="card-elevated mb-8">
                <CardContent className="p-8">
                  <p className="text-lg mb-6 text-muted-foreground leading-relaxed">
                    Nießbrauch ist ein dingliches Recht, das im Grundbuch eingetragen wird. Es erlaubt dem Berechtigten, eine Immobilie lebenslang zu nutzen oder die Erträge daraus zu ziehen – auch wenn er nicht mehr der Eigentümer ist.
                  </p>

                  <h3 className="text-xl font-semibold text-primary mb-4">Typischer Ablauf:</h3>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                      <span className="text-muted-foreground">Die Immobilie wird bereits zu Lebzeiten an Kinder oder Erben übertragen (Schenkung).</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                      <span className="text-muted-foreground">Der Schenker behält sich ein Nießbrauchrecht vor.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                      <span className="text-muted-foreground">Damit darf er die Immobilie weiter selbst bewohnen oder vermieten und die Erträge behalten.</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container-comfortable">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-12 text-center">
              Ihre Vorteile beim Nießbrauch
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {advantages.map((advantage, index) => (
                <Card key={index} className="card-elevated h-full text-center">
                  <CardHeader>
                    <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
                      <advantage.icon className="h-8 w-8 text-accent" />
                    </div>
                    <CardTitle className="text-xl text-primary">
                      {advantage.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base leading-relaxed">
                      {advantage.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Target Group Section */}
        <section className="py-16 md:py-24">
          <div className="container-comfortable">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-8 text-center">
                Für wen eignet sich Nießbrauch?
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="card-elevated">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-accent/10 p-3 rounded-lg">
                        <Users className="h-6 w-6 text-accent" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-primary mb-2">Familienorientierte Eigentümer</h3>
                        <p className="text-muted-foreground">Die die Immobilie in der Familie halten wollen.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="card-elevated">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-accent/10 p-3 rounded-lg">
                        <TrendingUp className="h-6 w-6 text-accent" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-primary mb-2">Senioren mit Steueroptimierung</h3>
                        <p className="text-muted-foreground">Die Schenkung und Nießbrauch kombinieren möchten.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="card-elevated">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-accent/10 p-3 rounded-lg">
                        <FileText className="h-6 w-6 text-accent" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-primary mb-2">Eltern mit Kindern</h3>
                        <p className="text-muted-foreground">Die den Nachlass frühzeitig regeln wollen.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="card-elevated">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-accent/10 p-3 rounded-lg">
                        <Euro className="h-6 w-6 text-accent" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-primary mb-2">Vermögensbesitzer</h3>
                        <p className="text-muted-foreground">Die weiterhin Einnahmen aus der Immobilie erzielen möchten.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Example Calculations */}
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container-comfortable">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-12 text-center">
              Beispielrechnungen & Praxisfälle
            </h2>

            <div className="grid lg:grid-cols-3 gap-8">
              {examples.map((example, index) => (
                <Card key={index} className="card-elevated">
                  <CardHeader>
                    <CardTitle className="text-xl text-primary">{example.title}</CardTitle>
                    <div className="text-2xl font-bold text-accent">{example.value}</div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Nießbrauchwert:</p>
                        <p className="font-semibold">{example.niessbrauchValue}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Steuerlich relevant:</p>
                        <p className="font-semibold">{example.taxRelevantValue}</p>
                      </div>
                    </div>
                    <div className="pt-4 border-t border-border">
                      <p className="text-sm font-medium text-primary">→ {example.benefit}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-16 md:py-24">
          <div className="container-comfortable">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
                Der Ablauf bei renteplusimmobilie.de
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                In 4 einfachen Schritten zu Ihrem Nießbrauch
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="card-elevated text-center">
                <CardHeader>
                  <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
                    <Users className="h-8 w-8 text-accent" />
                  </div>
                  <CardTitle className="text-xl">1. Kostenlose Erstberatung</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Im Gespräch wird geprüft, ob Nießbrauch, Leibrente, Teilverkauf oder Rückmiete besser passt.</p>
                </CardContent>
              </Card>

              <Card className="card-elevated text-center">
                <CardHeader>
                  <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
                    <Calculator className="h-8 w-8 text-accent" />
                  </div>
                  <CardTitle className="text-xl">2. Immobilienbewertung</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Zur steuerlichen Optimierung ist eine genaue Wertermittlung der Immobilie notwendig.</p>
                </CardContent>
              </Card>

              <Card className="card-elevated text-center">
                <CardHeader>
                  <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
                    <FileText className="h-8 w-8 text-accent" />
                  </div>
                  <CardTitle className="text-xl">3. Vertragsgestaltung</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Der Nießbrauch wird im notariellen Schenkungsvertrag geregelt und im Grundbuch gesichert.</p>
                </CardContent>
              </Card>

              <Card className="card-elevated text-center">
                <CardHeader>
                  <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
                    <Shield className="h-8 w-8 text-accent" />
                  </div>
                  <CardTitle className="text-xl">4. Übertragung & Absicherung</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Die Immobilie geht rechtlich auf die Kinder/Erben über, während das Nießbrauchrecht beim bisherigen Eigentümer verbleibt.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container-comfortable">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
                Häufige Fragen zum Nießbrauch
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Die wichtigsten Antworten zu Ihren Fragen rund um den Nießbrauch
              </p>
            </div>

            <div className="max-w-4xl mx-auto space-y-6">
              {faqItems.map((item, index) => (
                <Card key={index} className="card-elevated">
                  <CardHeader>
                    <CardTitle className="text-xl text-primary">{item.question}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground leading-relaxed">{item.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Why Us Section */}
        <section className="py-16 md:py-24">
          <div className="container-comfortable">
            <div className="text-center mb-12">
              {/* Founder Image */}
              <div className="mb-8">
                <img 
                  src={janineHardiImage} 
                  alt="Janine Hardi - Gründerin von RentePlusImmobilie" 
                  className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover mx-auto shadow-lg"
                />
                <p className="text-sm text-muted-foreground mt-3">Janine Hardi, Gründerin von RentePlusImmobilie</p>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4 text-center">
                Warum RentePlusImmobilie
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Ihr vertrauensvoller Partner für professionelle Immobilienverrentung
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="card-elevated text-center">
                <CardHeader className="pb-4">
                  <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
                    <Users className="h-8 w-8 text-accent" />
                  </div>
                  <CardTitle className="text-xl">Kostenlose Beratung</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Unverbindliche Erstberatung zu allen Verrentungsoptionen</p>
                </CardContent>
              </Card>

              <Card className="card-elevated text-center">
                <CardHeader className="pb-4">
                  <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
                    <Calculator className="h-8 w-8 text-accent" />
                  </div>
                  <CardTitle className="text-xl">Faire Bewertung</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Professionelle Immobilienbewertung für optimale Ergebnisse</p>
                </CardContent>
              </Card>

              <Card className="card-elevated text-center">
                <CardHeader className="pb-4">
                  <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
                    <Award className="h-8 w-8 text-accent" />
                  </div>
                  <CardTitle className="text-xl">Erfahrung</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Langjährige Expertise in der Immobilienverrentung</p>
                </CardContent>
              </Card>

              <Card className="card-elevated text-center">
                <CardHeader className="pb-4">
                  <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
                    <Shield className="h-8 w-8 text-accent" />
                  </div>
                  <CardTitle className="text-xl">Sicherheit</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Rechtlich abgesicherte Verträge und vertrauensvolle Abwicklung</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </main>

      <ProvenExpertBadge />
      <ContactSection />
      <HeyflowBanner />
      </div>
    </>
  );
};

export default Niessbrauch;